import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { observeElement } from '@/lib/observe'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface MarqueeProps {
    children: ReactNode
    speed?: number
    direction?: 'left' | 'right'
    copies?: number
    slowOnHover?: boolean
    className?: string
    trackClassName?: string
}

/**
 * Endless ticker driven by requestAnimationFrame so the speed can ease
 * down smoothly on hover instead of jumping like a CSS animation would.
 */
export const Marquee: FC<MarqueeProps> = ({
    children,
    speed = 60,
    direction = 'left',
    copies = 3,
    slowOnHover = true,
    className,
    trackClassName
}) => {
    const rootRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const firstRef = useRef<HTMLDivElement>(null)
    const reduced = useReducedMotion()

    useEffect(() => {
        const root = rootRef.current
        const track = trackRef.current
        const first = firstRef.current

        if (!root || !track || !first || reduced) return

        let width = first.getBoundingClientRect().width
        let offset = 0
        let current = speed
        let target = speed
        let last = 0
        let raf = 0

        const step = (now: number) => {
            const delta = Math.min(64, now - last) / 1000

            last = now
            current += (target - current) * 0.06

            if (width > 0) {
                offset = (offset + current * delta) % width

                const x = direction === 'left' ? -offset : offset - width

                track.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0)`
            }

            raf = window.requestAnimationFrame(step)
        }

        const start = () => {
            if (raf) return

            last = performance.now()
            raf = window.requestAnimationFrame(step)
        }

        const stop = () => {
            window.cancelAnimationFrame(raf)
            raf = 0
        }

        const resize = new ResizeObserver(() => {
            width = first.getBoundingClientRect().width
        })

        resize.observe(first)

        const unobserve = observeElement(root, entry => {
            if (entry.isIntersecting) start()
            else stop()
        }, { rootMargin: '120px' })

        const slow = () => {
            if (slowOnHover) target = speed * 0.18
        }

        const resume = () => {
            target = speed
        }

        root.addEventListener('pointerenter', slow)
        root.addEventListener('pointerleave', resume)

        return () => {
            stop()
            unobserve()
            resize.disconnect()
            root.removeEventListener('pointerenter', slow)
            root.removeEventListener('pointerleave', resume)
            track.style.transform = ''
        }
    }, [speed, direction, slowOnHover, reduced])

    return (
        <div
            ref={rootRef}
            className={cn('overflow-hidden', className)}
        >
            <div
                ref={trackRef}
                className={cn('flex w-max will-change-transform', trackClassName)}
            >
                {Array.from({ length: copies }).map((_, index) => (
                    <div
                        key={index}
                        ref={index === 0 ? firstRef : undefined}
                        aria-hidden={index > 0 ? 'true' : undefined}
                        className='flex shrink-0 items-center'
                    >
                        {children}
                    </div>
                ))}
            </div>
        </div>
    )
}
